<template>
  <div class="stores-page">
    <!-- 顶部 hero 搜索区 -->
    <div class="hero">
      <div class="hero-bg"></div>
      <div class="hero-content">
        <div class="hero-header">
          <div class="hero-icon">
            <i class="el-icon-map-location"></i>
          </div>
          <div class="hero-text">
            <h1 class="page-title">{{ $t('stores.pageTitle') }}</h1>
            <p class="page-subtitle">{{ $t('stores.pageSubtitle') }}</p>
          </div>
        </div>

        <div class="search-box">
          <el-select
            v-model="form.country"
            class="country-select"
            filterable
            :placeholder="$t('stores.countryPlaceholder')"
          >
            <el-option
              v-for="country in countryList"
              :key="country.code"
              :label="country.name"
              :value="country.code"
            >
              <span>{{ country.name }}</span>
              <span class="country-carriers">
                <span v-if="country.fedex_supported" class="carrier-mark fedex">FedEx</span>
                <span v-if="country.ups_supported" class="carrier-mark ups">UPS</span>
              </span>
            </el-option>
          </el-select>
          <div class="search-input-wrap">
            <i class="el-icon-location-outline search-input-icon"></i>
            <input
              v-model="form.zipcode"
              type="text"
              maxlength="10"
              :placeholder="$t('stores.zipcodePlaceholder')"
              class="search-input"
              @keyup.enter="onSearch"
            />
            <span
              v-if="form.zipcode"
              class="search-input-clear"
              @click="form.zipcode = ''"
            >
              <i class="el-icon-circle-close"></i>
            </span>
          </div>
          <button
            class="search-btn"
            :class="{ disabled: !isSearchValid || loading }"
            :disabled="!isSearchValid || loading"
            @click="onSearch"
          >
            <i :class="loading ? 'el-icon-loading' : 'el-icon-search'"></i>
            <span>{{ loading ? $t('stores.searching') : $t('stores.searchBtn') }}</span>
          </button>
        </div>

        <div v-if="searched && resultMeta && !loading" class="result-meta">
          <i class="el-icon-place"></i>
          <span class="loc" v-if="resultLocation">
            {{ resultLocation }}
          </span>
          <span class="country-pill">{{ resultMeta.country }}</span>
          <span class="zip-pill">{{ resultMeta.zipcode }}</span>
          <span class="counts" v-if="!hasErrors">
            <span class="count-pill fedex">
              <span class="dot"></span>FedEx · {{ fedexTotal }}
            </span>
            <span class="count-pill ups">
              <span class="dot"></span>UPS · {{ upsTotal }}
            </span>
          </span>
        </div>
      </div>
    </div>

    <!-- 初始空态 -->
    <div v-if="!searched && !loading" class="empty-state initial">
      <div class="empty-illust">
        <i class="el-icon-search"></i>
      </div>
      <h3 class="empty-title">{{ $t('stores.initialTip') }}</h3>
      <p class="empty-desc">{{ $t('stores.pageSubtitle') }}</p>
      <div class="quick-zips">
        <span class="quick-label">{{ $t('stores.quickTry') }}</span>
        <button
          v-for="zip in quickZips"
          :key="zip.code"
          class="quick-zip-btn"
          @click="useQuickZip(zip)"
        >
          <span class="quick-zip-code">{{ zip.code }}</span>
          <span class="quick-zip-name">{{ zip.name }}</span>
        </button>
      </div>
    </div>

    <!-- 全部为空 -->
    <div
      v-else-if="searched && !loading && fedexTotal === 0 && upsTotal === 0 && !hasErrors"
      class="empty-state notfound"
    >
      <div class="empty-illust">
        <i class="el-icon-warning-outline"></i>
      </div>
      <h3 class="empty-title">{{ $t('stores.notFoundTip') }}</h3>
    </div>

    <!-- 错误提示 -->
    <el-alert
      v-if="globalError"
      :title="$t('stores.errorPrefix') + globalError"
      type="error"
      show-icon
      :closable="false"
      class="global-error"
    />

    <!-- 选项卡：FedEx / UPS 切换 -->
    <div v-show="searched || loading" class="results-panel">
      <!-- 选项卡头 -->
      <div class="tabs-bar">
        <button
          class="tab-btn"
          :class="{ active: activeTab === 'fedex', fedex: true }"
          @click="activeTab = 'fedex'"
        >
          <span class="tab-brand-pill fedex">FedEx</span>
          <span class="tab-title">{{ $t('stores.fedexTitle') }}</span>
          <span v-if="!loading" class="tab-count fedex">{{ fedexTotal }}</span>
          <i v-if="errors.fedex" class="el-icon-warning-outline tab-err" :title="errors.fedex"></i>
        </button>
        <button
          class="tab-btn"
          :class="{ active: activeTab === 'ups', ups: true }"
          @click="activeTab = 'ups'"
        >
          <span class="tab-brand-pill ups">UPS</span>
          <span class="tab-title">{{ $t('stores.upsTitle') }}</span>
          <span v-if="!loading" class="tab-count ups">{{ upsTotal }}</span>
          <i v-if="errors.ups" class="el-icon-warning-outline tab-err" :title="errors.ups"></i>
        </button>
      </div>

      <!-- Tab 内容区 -->
      <div class="tab-body" v-loading="loading" element-loading-background="rgba(255,255,255,0.6)">
        <el-skeleton v-if="loading" :rows="6" animated />
        <template v-else>
          <!-- FedEx -->
          <div v-show="activeTab === 'fedex'">
            <div v-if="fedexTotal === 0" class="empty-card">
              <i class="el-icon-box"></i>
              <p>{{ $t('stores.fedexEmpty') }}</p>
            </div>
            <div v-else class="card-grid">
              <store-card
                v-for="item in fedexPagedList"
                :key="'f-' + (item.store_no || item.address)"
                :store="item"
                carrier="fedex"
              />
            </div>
            <div v-if="fedexTotal > pageSize" class="pager">
              <el-pagination
                background
                layout="prev, pager, next"
                :total="fedexTotal"
                :page-size="pageSize"
                :current-page.sync="fedexPage"
              />
            </div>
          </div>

          <!-- UPS -->
          <div v-show="activeTab === 'ups'">
            <div v-if="upsTotal === 0" class="empty-card">
              <i class="el-icon-box"></i>
              <p>{{ $t('stores.upsEmpty') }}</p>
            </div>
            <div v-else class="card-grid">
              <store-card
                v-for="item in upsPagedList"
                :key="'u-' + (item.store_no || item.address)"
                :store="item"
                carrier="ups"
              />
            </div>
            <div v-if="upsTotal > pageSize" class="pager">
              <el-pagination
                background
                layout="prev, pager, next"
                :total="upsTotal"
                :page-size="pageSize"
                :current-page.sync="upsPage"
              />
            </div>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import StoreCard from './StoreCard.vue';
import { findStores, storeCountries } from '@/api/stores';

export default {
  name: 'StoresIndex',
  components: { StoreCard },
  data() {
    return {
      form: {
        zipcode: '',
        country: 'US',
      },
      countryList: [{ name: 'United States', code: 'US', fedex_supported: true, ups_supported: true }],
      activeTab: 'fedex',
      loading: false,
      searched: false,
      fedexList: [],
      upsList: [],
      fedexPage: 1,
      upsPage: 1,
      pageSize: 12,
      resultMeta: null, // {zipcode, country, city, state}
      errors: {},
      globalError: '',
      quickZips: [
        { code: '90220', name: 'Compton, CA' },
        { code: '10001', name: 'New York, NY' },
        { code: '60601', name: 'Chicago, IL' },
        { code: '94102', name: 'San Francisco, CA' },
      ],
    };
  },
  computed: {
    isZipValid() {
      const zipcode = String(this.form.zipcode || '').trim();
      return /^[A-Z0-9](?:[A-Z0-9 -]{0,8}[A-Z0-9])?$/i.test(zipcode);
    },
    isSearchValid() {
      return !!this.form.country && this.isZipValid;
    },
    resultLocation() {
      if (!this.resultMeta) return '';
      return [this.resultMeta.city, this.resultMeta.state].filter(Boolean).join(', ');
    },
    fedexTotal() {
      return this.fedexList.length;
    },
    upsTotal() {
      return this.upsList.length;
    },
    fedexPagedList() {
      const start = (this.fedexPage - 1) * this.pageSize;
      return this.fedexList.slice(start, start + this.pageSize);
    },
    upsPagedList() {
      const start = (this.upsPage - 1) * this.pageSize;
      return this.upsList.slice(start, start + this.pageSize);
    },
    hasErrors() {
      return !!(this.errors.fedex || this.errors.ups);
    },
  },
  watch: {
    'form.zipcode'(val) {
      // 国际邮编允许字母、数字、空格和连字符，UPS 接口最长支持 10 位
      const cleaned = String(val || '')
        .toUpperCase()
        .replace(/[^A-Z0-9 -]/g, '')
        .slice(0, 10);
      if (cleaned !== val) {
        this.form.zipcode = cleaned;
      }
    },
  },
  mounted() {
    this.loadCountries();
  },
  methods: {
    onSearch() {
      if (!this.form.country) {
        this.$message.warning(this.$t('stores.countryRequired'));
        return;
      }
      if (!this.isZipValid) {
        this.$message.warning(this.$t('stores.zipcodeInvalid'));
        return;
      }
      this.fetchStores();
    },
    loadCountries() {
      storeCountries().then((res) => {
        if (!res || res.code !== 1 || !Array.isArray(res.data)) {
          this.$message.error((res && res.msg) || this.$t('stores.countryLoadFailed'));
          return;
        }
        this.countryList = res.data;
      }).catch((err) => {
        const message = (err && (err.msg || err.message)) || this.$t('stores.countryLoadFailed');
        this.$message.error(message);
      });
    },
    useQuickZip(zip) {
      this.form.country = 'US';
      this.form.zipcode = zip.code;
      this.$nextTick(() => this.fetchStores());
    },
    async fetchStores() {
      this.loading = true;
      this.errors = {};
      this.globalError = '';
      this.fedexPage = 1;
      this.upsPage = 1;

      try {
        const res = await findStores({
          zipcode: String(this.form.zipcode || '').trim(),
          country: this.form.country || 'US',
          carrier: 'all',
        });
        this.searched = true;

        if (!res || res.code !== 1) {
          this.globalError = (res && res.msg) || 'Unknown error';
          this.fedexList = [];
          this.upsList = [];
          this.resultMeta = null;
          return;
        }

        const data = res.data || {};
        this.fedexList = (data.fedex && data.fedex.list) || [];
        this.upsList = (data.ups && data.ups.list) || [];
        this.resultMeta = {
          zipcode: data.zipcode || this.form.zipcode,
          country: data.country || this.form.country,
          city: data.city || '',
          state: data.state || '',
        };
        // FedEx 没结果但 UPS 有，自动切到 UPS tab；反之同理
        if (this.fedexList.length === 0 && this.upsList.length > 0) {
          this.activeTab = 'ups';
        } else {
          this.activeTab = 'fedex';
        }
        this.errors = {};
        if (data.errors) {
          if (data.errors.fedex) this.errors.fedex = data.errors.fedex;
          if (data.errors.ups) this.errors.ups = data.errors.ups;
        }
      } catch (err) {
        this.globalError = (err && err.msg) || (err && err.message) || 'Network error';
        this.fedexList = [];
        this.upsList = [];
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
$fedex-color: #4d148c;
$fedex-color-2: #ff6600;
$ups-color: #351c15;
$ups-color-2: #ffb500;
$primary: #409eff;

.stores-page {
  background: #f5f7fa;
  box-sizing: border-box;
}

/* ============== 顶部 Hero 搜索区 ============== */
.hero {
  position: relative;
  background: linear-gradient(135deg, #1e88e5 0%, #6c5ce7 50%, #5e35b1 100%);
  border-radius: 16px;
  padding: 28px 32px 24px;
  margin-bottom: 24px;
  overflow: hidden;
  box-shadow: 0 8px 24px rgba(94, 53, 177, 0.18);
}

.hero-bg {
  position: absolute;
  inset: 0;
  background-image:
    radial-gradient(circle at 15% 30%, rgba(255, 255, 255, 0.18) 0%, transparent 40%),
    radial-gradient(circle at 85% 70%, rgba(255, 255, 255, 0.10) 0%, transparent 50%);
  pointer-events: none;
}

.hero-content {
  position: relative;
  z-index: 1;
}

.hero-header {
  display: flex;
  align-items: center;
  gap: 14px;
  margin-bottom: 20px;
  color: #fff;
}

.hero-icon {
  width: 44px;
  height: 44px;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.18);
  backdrop-filter: blur(8px);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 22px;
  color: #fff;
  flex-shrink: 0;
}

.hero-text {
  display: flex;
  flex-direction: column;
  justify-content: center;
  line-height: 1.2;
  flex: 1;
  min-width: 0;
  text-align: left;
}

.page-title {
  display: block;
  font-size: 20px;
  font-weight: 600;
  margin: 0 !important;
  padding: 0;
  letter-spacing: 0.5px;
  line-height: 1.3;
  color: #fff;
  text-align: left;
}

.page-subtitle {
  display: block;
  font-size: 13px;
  margin: 4px 0 0 !important;
  padding: 0;
  opacity: 0.85;
  line-height: 1.4;
  color: #fff;
  text-align: left;
}

/* ============== 搜索框 ============== */
.search-box {
  display: flex;
  align-items: stretch;
  gap: 12px;
}

.country-select {
  width: 220px;
  flex-shrink: 0;

  /deep/ .el-input__inner {
    height: 48px;
    line-height: 48px;
    border: none;
    border-radius: 10px;
    background: rgba(255, 255, 255, 0.96);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  }
}

.country-carriers {
  float: right;
}

.carrier-mark {
  display: inline-block;
  margin-left: 6px;
  padding: 0 5px;
  border-radius: 8px;
  font-size: 10px;
  line-height: 16px;
  color: #fff;
}

.carrier-mark.fedex {
  background: #4d148c;
}

.carrier-mark.ups {
  background: #8a6d00;
}

.search-input-wrap {
  position: relative;
  flex: 1;
}

.search-input {
  width: 100%;
  height: 48px;
  padding: 0 40px 0 44px;
  border: none;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.96);
  font-size: 16px;
  color: #303133;
  letter-spacing: 1px;
  outline: none;
  transition: box-shadow 0.2s, background 0.2s;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  box-sizing: border-box;
  font-family: inherit;

  &::placeholder {
    color: #a8abb2;
    font-size: 14px;
    letter-spacing: 0;
  }

  &:focus {
    background: #fff;
    box-shadow: 0 0 0 3px rgba(255, 255, 255, 0.4), 0 4px 12px rgba(0, 0, 0, 0.1);
  }
}

.search-input-icon {
  position: absolute;
  left: 16px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 18px;
  color: #909399;
  pointer-events: none;
}

.search-input-clear {
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 18px;
  color: #c0c4cc;
  cursor: pointer;
  transition: color 0.2s;

  &:hover {
    color: #606266;
  }
}

.search-btn {
  height: 48px;
  padding: 0 32px;
  border: none;
  border-radius: 10px;
  background: linear-gradient(135deg, #ff6b35 0%, #f7931e 100%);
  color: #fff;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: transform 0.15s, box-shadow 0.2s, opacity 0.2s, filter 0.2s;
  box-shadow: 0 4px 12px rgba(255, 107, 53, 0.35);
  white-space: nowrap;
  font-family: inherit;

  i {
    font-size: 16px;
  }

  &:hover:not(.disabled) {
    transform: translateY(-1px);
    box-shadow: 0 6px 16px rgba(255, 107, 53, 0.45);
    filter: brightness(1.05);
  }

  &:active:not(.disabled) {
    transform: translateY(0);
    box-shadow: 0 2px 8px rgba(255, 107, 53, 0.3);
  }

  &.disabled {
    background: rgba(255, 255, 255, 0.85);
    color: #c0c4cc;
    box-shadow: none;
    cursor: not-allowed;
  }
}

/* ============== 结果信息条 ============== */
.result-meta {
  margin-top: 16px;
  display: flex;
  align-items: center;
  gap: 10px;
  color: #fff;
  font-size: 14px;
  flex-wrap: wrap;

  i {
    font-size: 16px;
    opacity: 0.9;
  }

  .loc {
    font-weight: 500;
  }

  .country-pill,
  .zip-pill {
    display: inline-block;
    padding: 3px 10px;
    background: rgba(255, 255, 255, 0.2);
    border-radius: 6px;
    font-weight: 600;
    letter-spacing: 0.5px;
    backdrop-filter: blur(4px);
  }

  .counts {
    display: inline-flex;
    gap: 8px;
    margin-left: 8px;
  }

  .count-pill {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 3px 10px;
    border-radius: 12px;
    background: rgba(255, 255, 255, 0.95);
    font-size: 12px;
    font-weight: 600;
    color: #303133;

    .dot {
      width: 6px;
      height: 6px;
      border-radius: 50%;
    }

    &.fedex .dot { background: $fedex-color; }
    &.fedex { color: $fedex-color; }
    &.ups .dot { background: $ups-color-2; }
    &.ups { color: $ups-color; }
  }
}

@media (max-width: 768px) {
  .search-box {
    flex-wrap: wrap;
  }

  .country-select {
    width: 100%;
  }
}

/* ============== 空态 ============== */
.empty-state {
  background: #fff;
  border-radius: 12px;
  padding: 56px 24px 64px;
  text-align: center;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
}

.empty-illust {
  width: 96px;
  height: 96px;
  margin: 0 auto 20px;
  border-radius: 50%;
  background: linear-gradient(135deg, #ecf5ff 0%, #f5f3ff 100%);
  display: flex;
  align-items: center;
  justify-content: center;

  i {
    font-size: 44px;
    background: linear-gradient(135deg, #409eff, #6c5ce7);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }
}

.empty-title {
  font-size: 16px;
  font-weight: 600;
  color: #303133;
  margin: 0 0 8px;
}

.empty-desc {
  font-size: 13px;
  color: #909399;
  margin: 0 0 28px;
}

.quick-zips {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  flex-wrap: wrap;
  max-width: 620px;
  margin: 0 auto;

  .quick-label {
    color: #909399;
    font-size: 13px;
    margin-right: 4px;
  }
}

.quick-zip-btn {
  display: inline-flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 10px 16px;
  background: #fff;
  border: 1px solid #e4e7ed;
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.2s;
  font-family: inherit;
  text-align: left;

  &:hover {
    border-color: $primary;
    background: #f5fbff;
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(64, 158, 255, 0.15);
  }

  .quick-zip-code {
    font-size: 15px;
    font-weight: 700;
    color: #303133;
    letter-spacing: 0.5px;
    line-height: 1.2;
  }

  .quick-zip-name {
    font-size: 11px;
    color: #909399;
    margin-top: 2px;
  }
}

/* ============== 全局错误提示 ============== */
.global-error {
  margin-bottom: 16px;
}

/* ============== 选项卡 + 结果面板 ============== */
.results-panel {
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
  overflow: hidden;
  transition: box-shadow 0.2s;

  &:hover {
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.06);
  }
}

.tabs-bar {
  display: flex;
  border-bottom: 1px solid #ebeef5;
  background: #fafbfc;
  padding: 0 8px;
  gap: 4px;
}

.tab-btn {
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 14px 18px;
  background: transparent;
  border: none;
  border-bottom: 3px solid transparent;
  cursor: pointer;
  font-family: inherit;
  font-size: 14px;
  color: #606266;
  transition: all 0.2s;
  margin-bottom: -1px;

  &:hover:not(.active) {
    color: #303133;
    background: rgba(255, 255, 255, 0.6);
  }

  .tab-title {
    font-weight: 600;
  }

  .tab-err {
    color: #f56c6c;
    font-size: 14px;
    cursor: help;
  }
}

.tab-btn.fedex.active {
  color: $fedex-color;
  border-bottom-color: $fedex-color;
  background: #fff;
}

.tab-btn.ups.active {
  color: $ups-color;
  border-bottom-color: $ups-color-2;
  background: #fff;
}

.tab-brand-pill {
  display: inline-block;
  padding: 3px 9px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.5px;

  &.fedex {
    background: $fedex-color;
    color: #fff;
  }
  &.ups {
    background: $ups-color;
    color: $ups-color-2;
  }
}

.tab-count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 26px;
  height: 20px;
  padding: 0 8px;
  border-radius: 10px;
  font-size: 11px;
  font-weight: 700;
  line-height: 1;
  font-variant-numeric: tabular-nums;

  &.fedex {
    background: linear-gradient(135deg, $fedex-color 0%, lighten($fedex-color, 12%) 100%);
    color: #fff;
    box-shadow: 0 1px 4px rgba(77, 20, 140, 0.25);
  }
  &.ups {
    background: linear-gradient(135deg, $ups-color-2 0%, #ffd76a 100%);
    color: $ups-color;
    box-shadow: 0 1px 4px rgba(255, 181, 0, 0.35);
  }
}

.tab-body {
  padding: 20px;
  min-height: 280px;
}

.card-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 14px;

  @media (max-width: 1600px) {
    grid-template-columns: repeat(3, 1fr);
  }
  @media (max-width: 1200px) {
    grid-template-columns: repeat(2, 1fr);
  }
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
}

.empty-card {
  text-align: center;
  padding: 56px 20px;
  color: #909399;

  i {
    font-size: 40px;
    opacity: 0.4;
    display: block;
    margin-bottom: 10px;
  }
  p {
    font-size: 13px;
    margin: 0;
  }
}

.pager {
  margin-top: 24px;
  display: flex;
  justify-content: center;
}
</style>
